import { motion } from 'framer-motion';
import { useState } from 'react';
import { Mail, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
      console.error('Contact error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen pt-32" data-testid="contact-page">
      {/* Hero Image */}
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_74b64d1e-0f50-41cd-a085-d4779dba4423/artifacts/d9gx9mja_PXL_20250825_100641051.jpg"
          alt="Woman overlooking scenic valley and reservoir"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#F9F8F6]" />
      </section>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#F9F8F6]" data-testid="contact-hero">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-widest font-bold text-[#3A4A3F]/60 mb-4">
              Get In Touch
            </p>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight text-[#2C332B] mb-8">
              We'd Love to
              <br />
              <span className="text-[#D4A373]">Hear From You</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#6C757D]">
              Have a question about our experiences? Want to discuss a custom booking? Just
              reach out—we're here to help.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              data-testid="contact-info"
            >
              <h2 className="font-serif text-3xl font-semibold text-[#2C332B] mb-8">
                Contact Information
              </h2>

              <div className="space-y-8 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3A4A3F] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold uppercase tracking-widest text-[#3A4A3F]/60 mb-2">
                      Email Us
                    </p>
                    <a
                      href="mailto:team@wild-thyme.co.uk"
                      className="text-lg text-[#D4A373] hover:text-[#C69363] transition-colors"
                      data-testid="contact-email"
                    >
                      team@wild-thyme.co.uk
                    </a>
                    <p className="text-sm text-[#6C757D] mt-1">
                      We typically respond within 24 hours
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#3A4A3F] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold uppercase tracking-widest text-[#3A4A3F]/60 mb-2">
                      Location
                    </p>
                    <p className="text-lg text-[#2C332B]">United Kingdom</p>
                    <p className="text-sm text-[#6C757D] mt-1">
                      Experiences across beautiful UK locations
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#3A4A3F] text-white rounded-3xl p-8">
                <h3 className="font-serif text-2xl font-semibold mb-4">Ready to Book?</h3>
                <p className="text-white/90 leading-relaxed mb-6">
                  If you're ready to book an experience, head to our booking page for a faster
                  process.
                </p>
                <Link
                  to="/book"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4A373] text-white rounded-full font-medium hover:bg-[#C69363] transition-all duration-300"
                  data-testid="contact-book-cta"
                >
                  Book an Experience
                </Link>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="bg-white rounded-3xl shadow-lg p-8"
              data-testid="contact-form-container"
            >
              <h2 className="font-serif text-3xl font-semibold text-[#2C332B] mb-6">
                Send Us a Message
              </h2>

              {submitted && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-xl mb-6"
                  data-testid="contact-success"
                >
                  <CheckCircle className="w-5 h-5" />
                  <p className="text-sm">Thank you! We'll get back to you soon.</p>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="space-y-2">
                  <Label htmlFor="contact-name">Name *</Label>
                  <Input
                    id="contact-name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="rounded-xl"
                    data-testid="contact-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-email">Email *</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="rounded-xl"
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-message">Message *</Label>
                  <Textarea
                    id="contact-message"
                    required
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="rounded-xl min-h-[160px]"
                    placeholder="Tell us how we can help..."
                    data-testid="contact-message-input"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-xl" data-testid="contact-error">
                    <AlertCircle className="w-5 h-5" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-6 text-lg font-medium bg-[#3A4A3F] hover:bg-[#2C332B] rounded-full"
                  data-testid="contact-submit-button"
                >
                  {submitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
